/**
 * Gen-Z student faculty feedback form — opens in a new tab.
 * Submissions cannot be edited later.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';

const CRITERIA = [
  { key: 'TEACHING_QUALITY', label: 'Teaching quality', vibe: 'Did class actually slap?' },
  { key: 'SUBJECT_KNOWLEDGE', label: 'Subject knowledge', vibe: 'Did they know their stuff?' },
  { key: 'COMMUNICATION', label: 'Communication', vibe: 'Was it clear, not chaotic?' },
  { key: 'PUNCTUALITY', label: 'Punctuality', vibe: 'On time energy?' },
  { key: 'DOUBT_RESOLUTION', label: 'Doubt resolution', vibe: 'Did questions get real answers?' },
  { key: 'COURSE_COVERAGE', label: 'Course coverage', vibe: 'Syllabus actually covered?' },
  { key: 'ASSESSMENT_FAIRNESS', label: 'Assessment fairness', vibe: 'Grading felt fair?' },
];

const MOODS = [
  { value: 1, emoji: '😩', label: 'Nope' },
  { value: 2, emoji: '😕', label: 'Meh' },
  { value: 3, emoji: '😐', label: 'Okay' },
  { value: 4, emoji: '😊', label: 'Good' },
  { value: 5, emoji: '🔥', label: 'Elite' },
];

export default function StudentFeedbackForm() {
  const [params] = useSearchParams();
  const subjectId = params.get('subjectId');
  const facultyId = params.get('facultyId');
  const cycleId = params.get('cycleId');
  const subjectName = params.get('subjectName') || 'Subject';
  const facultyName = params.get('facultyName') || 'Faculty';

  const [step, setStep] = useState(0);
  const [ratings, setRatings] = useState(() => (
    Object.fromEntries(CRITERIA.map((item) => [item.key, { rating: 0, comments: '' }]))
  ));
  const [strengths, setStrengths] = useState('');
  const [improvements, setImprovements] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const current = CRITERIA[step];
  const progress = useMemo(() => ((step + 1) / (CRITERIA.length + 1)) * 100, [step]);
  const ratedCount = CRITERIA.filter((item) => Number(ratings[item.key]?.rating) >= 1).length;
  const missingIds = !subjectId || !facultyId || !cycleId;

  const setRating = (criterion, rating) => {
    setRatings((prev) => ({ ...prev, [criterion]: { ...prev[criterion], rating } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (missingIds) {
      setError('This form is missing subject or cycle details. Close this tab and open Give feedback again.');
      return;
    }
    const payloadRatings = CRITERIA.map(({ key }) => ({
      criterion: key,
      rating: Number(ratings[key]?.rating) || 0,
      comments: (ratings[key]?.comments || '').trim() || undefined,
    })).filter((item) => item.rating >= 1 && item.rating <= 5);
    if (payloadRatings.length < CRITERIA.length) {
      setError('Tap a mood for every criterion before sending.');
      setStep(CRITERIA.findIndex((item) => Number(ratings[item.key]?.rating) < 1) || 0);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/feedback/submit', {
        subjectId,
        facultyId,
        cycleId,
        isAnonymous,
        ratings: payloadRatings,
        strengths: strengths.trim() || undefined,
        improvements: improvements.trim() || undefined,
        additionalComments: additionalComments.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      if (code === 'DUPLICATE_SUBMISSION' || err.response?.status === 409) {
        setError('Already submitted. Feedback is locked and cannot be changed.');
      } else {
        setError(err.response?.data?.error?.message || 'Could not submit feedback.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="genz-page">
        <div className="genz-inner">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-200/80 mb-2">360 Feedback · Student drop</p>
          <h1 className="text-3xl sm:text-4xl font-semibold text-white m-0">{subjectName}</h1>
          <p className="mt-2 text-violet-100/80">Faculty: {facultyName}</p>
          <p className="genz-lock mt-3">One take only. After you hit submit, this rating is locked forever.</p>

          <div className="genz-card mt-6">
            {missingIds ? (
              <p>This tab is missing the subject link. Go back to the dashboard and hit Give feedback again.</p>
            ) : done ? (
              <div className="text-center py-8 space-y-3">
                <p className="text-5xl">✨</p>
                <h2 className="text-2xl font-semibold text-white m-0">Locked in</h2>
                <p className="text-slate-300">Thanks. Your rating is saved and cannot be changed.</p>
                <button type="button" className="genz-submit" onClick={() => window.close()}>Close this tab</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <div className="genz-dots" aria-hidden="true">
                    {CRITERIA.map((item, index) => (
                      <span
                        key={item.key}
                        className={index === step ? 'on' : Number(ratings[item.key]?.rating) >= 1 ? 'done' : ''}
                      />
                    ))}
                    <span className={step >= CRITERIA.length ? 'on' : ''} />
                  </div>
                  <div className="flex justify-between text-xs text-slate-300 mb-2">
                    <span>{ratedCount}/{CRITERIA.length} rated</span>
                    <span>{step < CRITERIA.length ? `Vibe check ${step + 1} of ${CRITERIA.length}` : 'Final notes'}</span>
                  </div>
                  <div className="genz-progress"><span style={{ width: `${progress}%` }} /></div>
                </div>

                {error ? <div className="alert-error">{error}</div> : null}

                {step < CRITERIA.length ? (
                  <div className="genz-criterion">
                    <p className="text-lg font-semibold text-white m-0">{current.label}</p>
                    <p className="text-sm text-slate-300 m-0">{current.vibe}</p>
                    <div className="genz-rate">
                      {MOODS.map((mood) => (
                        <button
                          key={mood.value}
                          type="button"
                          className={Number(ratings[current.key]?.rating) === mood.value ? 'selected' : ''}
                          onClick={() => setRating(current.key, mood.value)}
                        >
                          <span className="block text-xl">{mood.emoji}</span>
                          {mood.label}
                        </button>
                      ))}
                    </div>
                    <input
                      className="genz-input"
                      maxLength={500}
                      placeholder="Optional comment — keep it real"
                      value={ratings[current.key]?.comments || ''}
                      onChange={(e) => setRatings((prev) => ({ ...prev, [current.key]: { ...prev[current.key], comments: e.target.value } }))}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <label className="block text-sm text-slate-200">
                      What they nailed
                      <textarea className="genz-input mt-2" rows={2} maxLength={1000} value={strengths} onChange={(e) => setStrengths(e.target.value)} />
                    </label>
                    <label className="block text-sm text-slate-200">
                      What could level up
                      <textarea className="genz-input mt-2" rows={2} maxLength={1000} value={improvements} onChange={(e) => setImprovements(e.target.value)} />
                    </label>
                    <label className="block text-sm text-slate-200">
                      Extra notes
                      <textarea className="genz-input mt-2" rows={2} maxLength={1000} value={additionalComments} onChange={(e) => setAdditionalComments(e.target.value)} />
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-200">
                      <input type="checkbox" checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} />
                      Stay anonymous
                    </label>
                  </div>
                )}

                <div className="flex gap-3">
                  {step > 0 ? (
                    <button type="button" className="btn-secondary" onClick={() => setStep((n) => n - 1)}>Back</button>
                  ) : null}
                  {step < CRITERIA.length ? (
                    <button
                      type="button"
                      className="genz-submit"
                      onClick={() => {
                        if (Number(ratings[current.key]?.rating) < 1) {
                          setError('Pick a mood to continue.');
                          return;
                        }
                        setError('');
                        setStep((n) => n + 1);
                      }}
                    >
                      Next vibe
                    </button>
                  ) : (
                    <button type="submit" disabled={submitting} className="genz-submit">
                      {submitting ? 'Sending…' : 'Submit & lock'}
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
